import React, { useEffect, useState } from "react";
import { Container, Box, Theme } from "@womp/kookie-ui";
import { usePageContext } from "../../renderer/usePageContext";

export { Page };

// IMPORTANT: keep the `import.meta.env.SSR` guard inlined at this call site.
// Rolldown builds the module graph from the dynamic `import()` expression
// before it evaluates `clientOnlyLazy`, so hiding the guard inside a helper
// pulled the entire client tree into `build/server/`. With the guard in the
// same source module as the `import(...)`, Vite DCEs the branch during SSR
// and drops ClientOnlyApp + its transitive dependencies from the server graph.
const ClientOnlyAppLazy: React.LazyExoticComponent<
  React.ComponentType<Record<string, never>>
> = import.meta.env.SSR
  ? ((() => null) as unknown as React.LazyExoticComponent<
      React.ComponentType<Record<string, never>>
    >)
  : React.lazy(() => import("../../ssr/ClientOnlyApp"));

function Content({ children }: { children: React.ReactNode }) {
  return (
    <Container size="4">
      <Box p="6">{children}</Box>
    </Container>
  );
}

function Page() {
  const { data, isHydration } = usePageContext();
  const [isClient, setIsClient] = useState(false);

  // Set isClient to true after hydration
  useEffect(() => {
    setIsClient(true);
  }, []);

  if (data.type === "hosting") return null;

  if (data.type === "not-found") {
    return (
      <Theme>
        <Content>
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                404 - Page Not Found
              </h1>
              <p className="text-gray-600">
                The page you're looking for doesn't exist.
              </p>
            </div>
          </div>
        </Content>
      </Theme>
    );
  }

  if (data.type === "client") {
    // Always render the same initial content for both server and client
    // Then update it after hydration
    if (!isClient) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="text-2xl font-semibold mb-4">Loading...</div>
            <div className="text-gray-600">Preparing client content</div>
          </div>
        </div>
      );
    }

    // After hydration, render the full client content
    return <ClientOnlyAppLazy />;
  }
}
